import React from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { motion } from "framer-motion";

const Analysis = () => {
  const { state } = useLocation();
  const { term, examType, data, schoolId, grade } = state || {};
  const { learners, subjects } = data || { learners: [], subjects: [] };

  // Group learners by stream
  const groupByStream = (learners) => {
    const streams = {};
    learners.forEach((learner) => {
      const stream = learner.stream || "No Stream";
      if (!streams[stream]) {
        streams[stream] = [];
      }
      streams[stream].push(learner);
    });
    return streams;
  };

  const streamsData = groupByStream(learners);

  // Calculate statistics for a group of learners
  const calculateStats = (learners) => {
    const subjectStats = {};
    const allMarks = [];

    // Initialize subject stats
    Array.from(subjects).forEach((subject) => {
      subjectStats[subject] = {
        marks: [],
        highest: { value: -Infinity, learner: null },
        lowest: { value: Infinity, learner: null },
      };
    });

    // Process each learner's marks
    learners.forEach((learner) => {
      Object.entries(learner.marks).forEach(([subject, mark]) => {
        const numericMark = Number(mark) || 0;
        subjectStats[subject].marks.push(numericMark);
        allMarks.push(numericMark);

        if (numericMark > subjectStats[subject].highest.value) {
          subjectStats[subject].highest.value = numericMark;
          subjectStats[subject].highest.learner = learner.learnerName;
        }
        if (numericMark < subjectStats[subject].lowest.value) {
          subjectStats[subject].lowest.value = numericMark;
          subjectStats[subject].lowest.learner = learner.learnerName;
        }
      });
    });

    // Calculate averages and other stats for each subject
    Object.keys(subjectStats).forEach((subject) => {
      const marks = subjectStats[subject].marks;
      const sum = marks.reduce((a, b) => a + b, 0);
      subjectStats[subject].average = marks.length ? sum / marks.length : 0;

      // Calculate median
      const sorted = [...marks].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      subjectStats[subject].median = sorted.length
        ? sorted.length % 2 !== 0
          ? sorted[mid]
          : (sorted[mid - 1] + sorted[mid]) / 2
        : 0;
    });

    // Calculate overall class statistics
    const total = allMarks.reduce((a, b) => a + b, 0);
    const mean = allMarks.length ? total / allMarks.length : 0;

    // Calculate median
    const sortedAllMarks = [...allMarks].sort((a, b) => a - b);
    const middle = Math.floor(sortedAllMarks.length / 2);
    const median = sortedAllMarks.length
      ? sortedAllMarks.length % 2 !== 0
        ? sortedAllMarks[middle]
        : (sortedAllMarks[middle - 1] + sortedAllMarks[middle]) / 2
      : 0;

    // Calculate grade distribution
    const gradeCounts = {
      A: allMarks.filter((m) => m >= 80).length,
      B: allMarks.filter((m) => m >= 60 && m < 80).length,
      C: allMarks.filter((m) => m >= 40 && m < 60).length,
      D: allMarks.filter((m) => m >= 20 && m < 40).length,
      E: allMarks.filter((m) => m < 20).length,
    };

    return {
      subjectStats,
      overallStats: {
        total,
        mean,
        median,
        gradeCounts,
      },
      topPerformers: learners
        .map((learner) => ({
          name: learner.learnerName,
          total: Object.values(learner.marks).reduce(
            (a, b) => a + (Number(b) || 0),
            0
          ),
          average:
            Object.values(learner.marks).reduce(
              (a, b) => a + (Number(b) || 0),
              0
            ) / Object.keys(learner.marks).length,
        }))
        .sort((a, b) => b.average - a.average)
        .slice(0, 3),
    };
  };

  // Calculate stats for each stream and overall
  const streamStats = {};
  Object.keys(streamsData).forEach((stream) => {
    streamStats[stream] = calculateStats(streamsData[stream]);
  });

  const overallStats = calculateStats(learners);

  // Prepare data for charts
  const prepareChartData = (stats) => {
    const colors = [
      "#FF6384",
      "#36A2EB",
      "#FFCE56",
      "#4BC0C0",
      "#9966FF",
      "#FF9F40",
      "#C9CBCF",
    ];
    return Array.from(subjects).map((subject, index) => ({
      name: subject,
      value: stats.subjectStats[subject]?.average || 0,
      color: colors[index % colors.length],
    }));
  };

  const prepareGradeData = (gradeCounts) => {
    return [
      { name: "A", count: gradeCounts.A },
      { name: "B", count: gradeCounts.B },
      { name: "C", count: gradeCounts.C },
      { name: "D", count: gradeCounts.D },
      { name: "E", count: gradeCounts.E },
    ];
  };

  if (!term || !data || !schoolId || !grade) {
    return (
      <Container
        maxWidth="md"
        sx={{ py: 4, bgcolor: "#f5f5f5", minHeight: "100vh" }}
      >
        <Typography
          variant="h6"
          sx={{ textAlign: "center", color: "#e74c3c" }}
        >
          Error: Missing required parameters
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{ py: 4, bgcolor: "#f5f5f5", minHeight: "100vh" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "#333", mb: 4, textAlign: "center" }}
        >
          Analysis for {examType || "All Exams"} - Term {term} - {grade}
        </Typography>

        {/* Overall Class Performance */}
        <Card
          sx={{
            bgcolor: "#fff",
            borderRadius: 2,
            p: 3,
            mb: 3,
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color: "#6200ee",
              mb: 2,
              borderBottom: "1px solid #eee",
              pb: 1,
            }}
          >
            Overall Class Performance
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Card
                sx={{
                  bgcolor: "#f9f9f9",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{ fontSize: "0.875rem", color: "#666", mb: 0.5 }}
                >
                  Average Mark
                </Typography>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: "bold", color: "#333" }}
                >
                  {overallStats.overallStats.mean.toFixed(2)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card
                sx={{
                  bgcolor: "#f9f9f9",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{ fontSize: "0.875rem", color: "#666", mb: 0.5 }}
                >
                  Median Mark
                </Typography>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: "bold", color: "#333" }}
                >
                  {overallStats.overallStats.median.toFixed(2)}
                </Typography>
              </Card>
            </Grid>
          </Grid>

          <Typography
            sx={{ fontSize: "1rem", fontWeight: "600", color: "#333", mt: 2, mb: 1 }}
          >
            Top Performers
          </Typography>
          {overallStats.topPerformers.map((performer, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                py: 1,
                borderBottom: "1px solid #eee",
              }}
            >
              <Typography sx={{ fontWeight: "bold", mr: 2, width: 30 }}>
                {index + 1}.
              </Typography>
              <Typography sx={{ flex: 1 }}>{performer.name}</Typography>
              <Typography sx={{ fontWeight: "bold", color: "#6200ee" }}>
                {performer.average.toFixed(2)}
              </Typography>
            </Box>
          ))}

          <Typography
            sx={{ fontSize: "1rem", fontWeight: "600", color: "#333", mt: 2, mb: 1 }}
          >
            Grade Distribution
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              bgcolor: "#fff",
              borderRadius: 2,
              p: 2,
            }}
          >
            <BarChart
              width={300}
              height={220}
              data={prepareGradeData(overallStats.overallStats.gradeCounts)}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#6200ee" barSize={20} />
            </BarChart>
          </Box>
        </Card>

        {/* Subject-wise Performance */}
        <Card
          sx={{
            bgcolor: "#fff",
            borderRadius: 2,
            p: 3,
            mb: 3,
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color: "#6200ee",
              mb: 2,
              borderBottom: "1px solid #eee",
              pb: 1,
            }}
          >
            Subject-wise Performance
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              bgcolor: "#fff",
              borderRadius: 2,
              p: 2,
              mb: 2,
            }}
          >
            <PieChart width={300} height={220}>
              <Pie
                data={prepareChartData(overallStats)}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label
              >
                {prepareChartData(overallStats).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </Box>
          {Array.from(subjects).map((subject) => (
            <Box
              key={subject}
              sx={{
                mb: 2,
                pb: 2,
                borderBottom: "1px solid #eee",
              }}
            >
              <Typography
                sx={{ fontSize: "1rem", fontWeight: "bold", color: "#333", mb: 1 }}
              >
                {subject}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography>
                    Avg:{" "}
                    {overallStats.subjectStats[subject]?.average.toFixed(2) ||
                      "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>
                    Median:{" "}
                    {overallStats.subjectStats[subject]?.median.toFixed(2) ||
                      "N/A"}
                  </Typography>
                </Grid>
              </Grid>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, px: 1 }}
              >
                <Typography
                  sx={{ fontSize: "0.875rem", color: "#666", fontWeight: "bold" }}
                >
                  Highest:
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: "#333" }}>
                  {overallStats.subjectStats[subject]?.highest.learner || "N/A"} (
                  {overallStats.subjectStats[subject]?.highest.value || "N/A"})
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", px: 1 }}
              >
                <Typography
                  sx={{ fontSize: "0.875rem", color: "#666", fontWeight: "bold" }}
                >
                  Lowest:
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: "#333" }}>
                  {overallStats.subjectStats[subject]?.lowest.learner || "N/A"} (
                  {overallStats.subjectStats[subject]?.lowest.value || "N/A"})
                </Typography>
              </Box>
            </Box>
          ))}
        </Card>

        {/* Stream-wise Performance */}
        {Object.keys(streamsData).length > 1 && (
          <Card
            sx={{
              bgcolor: "#fff",
              borderRadius: 2,
              p: 3,
              mb: 3,
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#6200ee",
                mb: 2,
                borderBottom: "1px solid #eee",
                pb: 1,
              }}
            >
              Stream-wise Performance
            </Typography>
            {Object.keys(streamStats).map((stream) => (
              <Box
                key={stream}
                sx={{
                  bgcolor: "#f9f9f9",
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: "bold", color: "#333", mb: 2 }}
                >
                  Stream: {stream}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Card
                      sx={{
                        bgcolor: "#fff",
                        borderRadius: 2,
                        p: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "0.875rem", color: "#666", mb: 0.5 }}
                      >
                        Average
                      </Typography>
                      <Typography
                        sx={{ fontSize: "1rem", fontWeight: "bold", color: "#333" }}
                      >
                        {streamStats[stream].overallStats.mean.toFixed(2)}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card
                      sx={{
                        bgcolor: "#fff",
                        borderRadius: 2,
                        p: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "0.875rem", color: "#666", mb: 0.5 }}
                      >
                        Median
                      </Typography>
                      <Typography
                        sx={{ fontSize: "1rem", fontWeight: "bold", color: "#333" }}
                      >
                        {streamStats[stream].overallStats.median.toFixed(2)}
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: "600", color: "#333", mb: 1 }}
                >
                  Top Performers
                </Typography>
                {streamStats[stream].topPerformers.map((performer, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Typography
                      sx={{ fontWeight: "bold", mr: 2, width: 30 }}
                    >
                      {index + 1}.
                    </Typography>
                    <Typography sx={{ flex: 1 }}>{performer.name}</Typography>
                    <Typography
                      sx={{ fontWeight: "bold", color: "#6200ee" }}
                    >
                      {performer.average.toFixed(2)}
                    </Typography>
                  </Box>
                ))}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    bgcolor: "#fff",
                    borderRadius: 2,
                    p: 2,
                    mt: 2,
                  }}
                >
                  <BarChart
                    width={300}
                    height={220}
                    data={prepareGradeData(
                      streamStats[stream].overallStats.gradeCounts
                    )}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#6200ee" barSize={20} />
                  </BarChart>
                </Box>
              </Box>
            ))}
            <Typography
              sx={{ fontSize: "1rem", fontWeight: "600", color: "#333", mb: 1 }}
            >
              Stream Ranking
            </Typography>
            {Object.entries(streamStats)
              .sort((a, b) => b[1].overallStats.mean - a[1].overallStats.mean)
              .map(([stream, stats], index) => (
                <Box
                  key={stream}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    py: 1.5,
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <Typography
                    sx={{ fontWeight: "bold", mr: 2, width: 30 }}
                  >
                    {index + 1}.
                  </Typography>
                  <Typography sx={{ flex: 1 }}>{stream}</Typography>
                  <Typography
                    sx={{ fontWeight: "bold", color: "#6200ee" }}
                  >
                    {stats.overallStats.mean.toFixed(2)}
                  </Typography>
                </Box>
              ))}
          </Card>
        )}
      </motion.div>
    </Container>
  );
};

export default Analysis;